import { AbstractBaseUser } from "@kvix/shared";
import { Button, Placeholder, Text } from "@kvix/ui";
import {
  Avatar,
  Box,
  makeStyles,
  Popper,
  TextField,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import Hashids from "hashids";
import queryString from "query-string";
import React, { useContext, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAsync } from "react-use";
import styled from "styled-components";
import { isInstructor, KvixUserContext } from "../../../../contexts/user";
import { useFetchRecruitments } from "../../../../hooks/http";
import rewardImg from "../../../partials/assets/recruitgift.png";
import { PaymentContext } from "../../Payment/context";
import { ProfileSection } from "../partials/Section";

const RECRUITMENT_GOAL = 1;

const useStyles = makeStyles((theme) => ({
  nameText: {
    fontWeight: 500,
    fontSize: "12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100px",
    textAlign: "center",
  },
  linkShareTextField: {
    "& .MuiOutlinedInput-root": {
      background: theme.palette.common.white,
      fontSize: "14px",
      height: "38px",
      color: theme.palette.common.black,
    },
    "& .MuiOutlinedInput-input": {
      textOverflow: "ellipsis",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.common.black,
    },
  },
  popper: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    opacity: 1,
    animation: "1000ms $fadeIn",
    animationTimingFunction: "ease",
  },

  "@keyframes fadeIn": {
    "100%": {
      opacity: 0,
      transform: "translateY(40%)",
    },
    "50%": {
      opacity: 1,
      transform: "translateY(40%)",
    },
    "0%": {
      opacity: 0,
    },
  },
}));

const RecruitmentList: React.FC<{
  recruitedUsers: AbstractBaseUser[];
  goalCompleted: boolean;
}> = (props) => {
  const { t, ready } = useTranslation("account");
  const theme = useTheme();
  const classes = useStyles(props);
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const totalRecruitmentList = [...Array(RECRUITMENT_GOAL).keys()].map((x) => {
    return (
      props.recruitedUsers[x] || {
        id: x,
        imageUrl: null,
        name: { first: "", last: "" },
      }
    );
  });

  if (!ready) {
    return null;
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="start"
      marginBottom="24px"
    >
      <Box display="flex">
        {totalRecruitmentList.map((user, index) => (
          <Box display="flex" flexDirection="column" key={index} mr={2}>
            <Avatar
              style={{ alignSelf: "center" }}
              src={user.imageUrl}
              imgProps={{
                loading: "lazy",
              }}
            />
            {!isMobile && (
              <Text className={classes.nameText}>{`${
                user.name.first
              } ${user.name.last.substr(0, 1)}`}</Text>
            )}
          </Box>
        ))}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        textAlign="center"
        style={{ filter: !props.goalCompleted ? "opacity(0.2)" : "none" }}
      >
        <img
          style={{
            height: "40px",
            width: "60px",
            alignSelf: "center",
          }}
          src={rewardImg}
        />
        <Text style={{ fontWeight: 600, fontSize: "12px" }}>
          {t("recruitments:kvixPremium")}
        </Text>
      </Box>
    </Box>
  );
};

const LinkShareContainer = styled(Box)`
  background: #fee9ee;
  border-radius: 12px;
  padding: 12px;
`;

const LinkShareContainerTitle = styled(Text)`
  font-size: 14px;
  margin-bottom: 16px;
`;

const LinkShareContainerText = styled(Text)`
  font-size: 14px;
  margin-bottom: 8px;
  color: #000;
`;

export const ProfilePageRecruitments: React.FC = (props) => {
  const { t, ready } = useTranslation("account");
  const { user } = useContext(KvixUserContext);
  const recruitments = useFetchRecruitments();
  const { actions } = useContext(PaymentContext);
  const hashids = new Hashids("", 10);
  const recruitCode = hashids.encode(user.id);
  const [rewardCode, setRewardCode] = useState<string>(null);
  const classes = useStyles(props);
  const [popperOpen, setPopperOpen] = useState(false);
  const baseUrl = `https://${window.location.host}/recruitme`;

  const queryParams = {
    code: `${recruitCode}`,
    utm_source: "virality",
    utm_medium: "profile_page",
    utm_campaign: "invite_link",
    utm_content: `${user.id}`,
  };

  const recruitUrl = queryString.stringifyUrl({
    url: baseUrl,
    query: queryParams,
  });

  const goalCompleted = recruitments.value?.length === RECRUITMENT_GOAL;
  const copyButtonRef = useRef<HTMLSpanElement>();

  useAsync(async () => {
    setRewardCode(await actions.fetchRecruitRewardCode());
  }, []);

  const handleOpenPopper = () => {
    setPopperOpen(true);

    setTimeout(() => {
      setPopperOpen(false);
    }, 1000);
  };

  if (!ready) {
    return null;
  }

  return (
    <>
      <ProfileSection
        title={
          <Text variant="h4">
            {isInstructor(user)
              ? t("recruitments.subtitleInstructor")
              : t("recruitments.subtitle")}
          </Text>
        }
      >
        {!isInstructor(user) ? (
          <>
            {!recruitments.loading && recruitments.value ? (
              <>
                <RecruitmentList
                  recruitedUsers={recruitments.value}
                  goalCompleted={goalCompleted}
                />
                {goalCompleted ? (
                  <>
                    <Text
                      variant="h5"
                      gutterBottom
                      style={{ lineHeight: 1.25 }}
                    >
                      {t("recruitments.successfulRecruitments")}
                    </Text>
                    {rewardCode ? (
                      <Link to={`/redeem?code=${rewardCode}`}>
                        <Text
                          variant="h5"
                          color="primary"
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                            display: "inline",
                          }}
                        >
                          {t("recruitments.cashInRewardHere")}
                        </Text>
                      </Link>
                    ) : (
                      <Text variant="h5">
                        {t("recruitments.freeMonthUsed")}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text variant="h5">
                    {t("recruitments.recruitmentsSoFar", {
                      count: recruitments.value.length,
                    })}
                  </Text>
                )}
              </>
            ) : (
              <Placeholder.RecruitmentAvatars />
            )}
          </>
        ) : (
          <Text variant="h5" style={{ marginBottom: "24px" }}>
            {t("recruitments.recruitmentsSoFarInstructor", {
              numberOfRecruitments: recruitments?.value?.length,
            })}
          </Text>
        )}
        {!isInstructor(user) && (
          <Text style={{ margin: "36px 0 24px 0" }}>
            {t("recruitments.text")}
            <span style={{ fontWeight: 500 }}>
              {t("recruitments.kvixPremium")}
            </span>{" "}
            {t("recruitments.text2")}
          </Text>
        )}
        <LinkShareContainer>
          <LinkShareContainerTitle color="primary">
            {t("recruitments.shareLink")}
          </LinkShareContainerTitle>
          <LinkShareContainerText>
            {isInstructor(user)
              ? t("recruitments.shareLinkTextInstructor")
              : t("recruitments.shareLinkText")}
          </LinkShareContainerText>
          <Box display="flex" width="100%">
            <TextField
              inputProps={{ spellCheck: false }}
              type="readOnly"
              focused={false}
              variant="outlined"
              className={classes.linkShareTextField}
              value={recruitUrl}
              fullWidth
            />
            <CopyToClipboard text={recruitUrl}>
              <span ref={copyButtonRef}>
                <Button
                  onClick={handleOpenPopper}
                  variant="contained"
                  style={{ height: "38px", marginLeft: "5px" }}
                >
                  {t("recruitments.copy")}
                </Button>
              </span>
            </CopyToClipboard>
          </Box>
        </LinkShareContainer>
      </ProfileSection>

      <Popper open={popperOpen} anchorEl={copyButtonRef.current} transition>
        <div className={classes.popper}>{t("recruitments.copied")}</div>
      </Popper>
    </>
  );
};
